import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Edit2, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const EditableDataPreview = ({ data, onDataUpdate }) => {
  const [editableData, setEditableData] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    if (data && data.preview) {
      setEditableData(JSON.parse(JSON.stringify(data.preview)));
    }
  }, [data]);

  if (!data || !data.preview || data.preview.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        No data to preview
      </div>
    );
  }

  const { document_type } = data;

  const startEdit = (rowIndex, field, currentValue) => {
    setEditingCell({ rowIndex, field });
    setEditValue(currentValue || '');
  };

  const saveEdit = () => {
    if (editingCell) {
      const { rowIndex, field } = editingCell;
      const newData = [...editableData];
      newData[rowIndex][field] = editValue;
      setEditableData(newData);
      setEditingCell(null);
      
      // Notify parent component of the update
      if (onDataUpdate) {
        onDataUpdate(newData);
      }
      
      toast.success('Value updated');
    }
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const renderEditableCell = (rowIndex, field, value, isLowConfidence) => {
    const isEditing = editingCell && editingCell.rowIndex === rowIndex && editingCell.field === field;

    return (
      <TableCell
        className={`group relative ${isLowConfidence ? 'bg-yellow-50' : ''}`}
        data-testid={`cell-${rowIndex}-${field}`}
      >
        {isEditing ? (
          <div className="flex items-center space-x-2">
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="h-8 text-sm"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveEdit();
                if (e.key === 'Escape') cancelEdit();
              }}
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={saveEdit}
              data-testid={`save-edit-${rowIndex}-${field}`}
            >
              <Check className="w-4 h-4 text-green-600" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={cancelEdit}
            >
              <X className="w-4 h-4 text-red-600" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className={typeof value === 'number' ? 'font-medium' : ''}>
              {(() => {
                // Check if field should be formatted as currency
                const isCurrencyField = field.includes('pay') || field.includes('debit') || 
                                       field.includes('credit') || field.includes('balance') || 
                                       field.includes('tax') || field.includes('ni');
                
                if (isCurrencyField && typeof value === 'number') {
                  return `£${value.toFixed(2)}`;
                } else if (value !== null && value !== undefined && value !== '') {
                  return value;
                } else {
                  return '-';
                }
              })()}
            </span>
            <button
              onClick={() => startEdit(rowIndex, field, value)}
              className="opacity-0 group-hover:opacity-100 transition-opacity ml-2"
              data-testid={`edit-${rowIndex}-${field}`}
            >
              <Edit2 className="w-3 h-3 text-slate-400 hover:text-blue-600" />
            </button>
          </div>
        )}
      </TableCell>
    );
  };

  // Render bank statement preview
  const renderBankStatementPreview = () => (
    <ScrollArea className="w-full rounded-lg border border-slate-200">
      <div className="min-w-[800px]">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">Date</TableHead>
              <TableHead className="font-semibold">Description</TableHead>
              <TableHead className="font-semibold text-right">Debit</TableHead>
              <TableHead className="font-semibold text-right">Credit</TableHead>
              <TableHead className="font-semibold text-right">Balance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {editableData.map((row, index) => (
              <TableRow key={index} data-testid={`preview-row-${index}`}>
                {renderEditableCell(index, 'date', row.date, row.confidence === 'low')}
                {renderEditableCell(index, 'description', row.description, row.confidence === 'low')}
                {renderEditableCell(index, 'debit', row.debit, row.confidence === 'low')}
                {renderEditableCell(index, 'credit', row.credit, row.confidence === 'low')}
                {renderEditableCell(index, 'balance', row.balance, row.confidence === 'low')}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </ScrollArea>
  );

  // Render payslip preview
  const renderPayslipPreview = () => (
    <ScrollArea className="w-full rounded-lg border border-slate-200">
      <div className="min-w-[1000px]">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">Employee</TableHead>
              <TableHead className="font-semibold">Company</TableHead>
              <TableHead className="font-semibold">Pay Date</TableHead>
              <TableHead className="font-semibold text-right">Gross Pay</TableHead>
              <TableHead className="font-semibold text-right">Tax</TableHead>
              <TableHead className="font-semibold text-right">NI</TableHead>
              <TableHead className="font-semibold text-right">Net Pay</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {editableData.map((row, index) => (
              <TableRow key={index} data-testid={`preview-row-${index}`}>
                {renderEditableCell(index, 'employee_name', row.employee_name, row.confidence === 'low')}
                {renderEditableCell(index, 'company_name', row.company_name, row.confidence === 'low')}
                {renderEditableCell(index, 'pay_date', row.pay_date, row.confidence === 'low')}
                {renderEditableCell(index, 'gross_pay', row.gross_pay, row.confidence === 'low')}
                {renderEditableCell(index, 'tax', row.tax, row.confidence === 'low')}
                {renderEditableCell(index, 'ni', row.ni, row.confidence === 'low')}
                {renderEditableCell(index, 'net_pay', row.net_pay, row.confidence === 'low')}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </ScrollArea>
  );

  return (
    <div data-testid="editable-data-preview" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-600">
            Showing first {editableData.length} of {data.total_records} record{data.total_records > 1 ? 's' : ''}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            <span className="inline-block w-3 h-3 bg-yellow-50 border border-yellow-200 mr-1"></span>
            Yellow highlighted cells have low confidence - click to edit
          </p>
        </div>
        <div className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
          {document_type === 'bank_statement' ? 'Bank Statement' : 'Payslip'}
        </div>
      </div>

      {document_type === 'bank_statement'
        ? renderBankStatementPreview()
        : renderPayslipPreview()}
    </div>
  );
};

export default EditableDataPreview;
